import React from 'react'
// libs
import { formValueSelector } from 'redux-form'
import { useSelector } from 'react-redux'
import styled, { css } from 'styled-components'
// components
import AddEntityForm from '../components/AddForm/AddEntityForm'
import AddRootEntityForm from '../components/AddForm/AddRootEntityForm'
import NodeContent from 'containers/ObjectivesList/components/NodeContent'
// context
import { ObjectivesListTreeBLContext } from 'containers/ObjectivesList/layers/BL/Tree'
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// constants
import { ENTITIES, Forms, NODE_CONTENT_RENDERER_SIZES } from 'constants/index'
// types
import { IPlanTypeLevelOption } from 'types/graphql'
// themes
import { colors } from 'themes'
// utils
import get from 'lodash/get'
import { isDescendant } from 'utils/plan-tree'
import { isColorLight } from 'utils/tree'
import { transparentize } from 'polished'
import { generateLogEntityType } from 'utils/activities'
// views
import { DotsIcon } from 'components/PageComponents/Plans/Edit/components/ObjectivesList/ObjectivesTreeTheme/NodeInfo'
import { CollapseIcon, ContentWrapper, IconsWrapper } from 'themes/views/objectivesList'

// styled
const Wrapper = styled.div<{
  $isKPI: boolean
  $isAction: boolean
  $background: string
  $isCancelPad: boolean
  $isLandingPad: boolean
  $isNodeSearched: boolean
  $isSearchActive: boolean
  $isArchived?: boolean
}>`
  display: flex;
  cursor: pointer;
  height: ${NODE_CONTENT_RENDERER_SIZES.NODE_HEIGHT}px;
  border-radius: 5px;

  ${({ $isNodeSearched, $isSearchActive }) => $isSearchActive && `opacity: ${$isNodeSearched ? 1 : 0.3}`};

  ${({ $background }) =>
    $background &&
    css`
      background: ${$background};

      &:hover {
        background: ${transparentize(0.2, $background)};
      }
    `};

  ${({ $background }) => isColorLight($background) && `border: 1px solid ${colors.gray290}`};

  ${({ $isAction, $isKPI }) => ($isAction || $isKPI) && `background: none;`};

  ${({ $isLandingPad }) =>
    $isLandingPad &&
    css`
      background: ${transparentize(0.7, colors.purple300)};
      border: 3px dotted ${colors.purple300};

      & > * {
        visibility: hidden;
      }
    `};

  ${({ $isCancelPad }) =>
    $isCancelPad &&
    css`
      background: ${transparentize(0.7, colors.red300)};
    `};

  ${({ $isArchived }) =>
    $isArchived &&
    css`
      border: 2px dashed ${colors.gray220};
      border-radius: 6px;
      background: ${colors.gray190};

      &:hover {
        background: ${colors.gray190};
        opacity: 0.8;
      }

      ${DotsIcon} {
        path {
          fill: ${colors.black};
        }
      }

      * {
        color: ${colors.black};
      }

      > div:first-child {
        background: none;
        border-right: 2px dashed ${colors.gray220};
      }
    `};
`

const selector = formValueSelector(Forms.PAGE_SEARCH_FORM)

const NodeContentRenderer = (props) => {
  const {
    node,
    path,
    tree,
    style,
    title,
    icons,
    treeId, // Not needed, but preserved for other renderers
    isOver, // Not needed, but preserved for other renderers
    buttons,
    setTree,
    didDrop,
    canDrag,
    canDrop,
    subtitle,
    swapFrom,
    swapDepth,
    treeIndex,
    parentNode, // Needed for dndManager
    isDragging,
    swapLength,
    draggedNode,
    paddingLeft,
    rowDirection,
    isSearchFocus,
    isSearchMatch,
    formMarginLeft,
    connectDragSource,
    connectDragPreview,
    scaffoldBlockPxWidth,
    toggleChildrenVisibility,
    ...otherProps
  } = props

  const { scrollToNode } = React.useContext(ObjectivesListTreeBLContext)
  const { fieldFilters } = React.useContext(PlanEditPageContext)

  const search = useSelector((state) => selector(state, 'description'))

  // methods
  const handleCollapseClick = (event: React.MouseEvent) => {
    event.stopPropagation()

    toggleChildrenVisibility({ node, path, treeIndex })
  }

  const connectDrag = (ref: React.RefObject<any>) => {
    if (canDrag) {
      connectDragSource(ref, { dropEffect: 'copy' })
      connectDragPreview(ref)
    }
  }

  const handleActionAdd = async (event: React.MouseEvent) => {
    event.stopPropagation()

    await setTree(node.addCreateActionForm(tree))
    scrollToNode(node)
  }

  const handleKPIAdd = async (event: React.MouseEvent) => {
    event.stopPropagation()

    await setTree(node.addCreateKPIForm(tree))
    scrollToNode(node)
  }

  const handleObjectiveAdd = async (event: React.MouseEvent, typeOption?: IPlanTypeLevelOption) => {
    event.stopPropagation()

    const nextLevelDescription = get(node, 'nextLevel.description')

    await setTree(node.addCreateObjectiveForm(tree, nextLevelDescription, typeOption))
    scrollToNode(node)
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (document.getElementById('PopoverModal')) {
      return
    }
    node.handleClick({ parentPermissions: node.entity.parentPermissions, color: node.color })(e)
  }

  // constants
  const hasFilter = !!fieldFilters?.length

  const isNodeSearched = React.useMemo(() => {
    const matchesBackend = !hasFilter || node.matchesFilter !== false

    if (search && node.title) {
      return matchesBackend && node.title.toLowerCase().includes(search.toLowerCase())
    }

    if (hasFilter) {
      return matchesBackend
    }
  }, [search, hasFilter, node.matchesFilter])

  const nodeTitle = title || node.title

  const isBeingDragged = draggedNode && isDescendant(draggedNode, node)
  const isLandingPadActive = !didDrop && isDragging

  const color = get(node, 'color')
  const hasChildren = !!get(node, 'children.length')

  const nextLevelDescription =
    get(node, 'newEntityLevel.description') === get(node, 'rootLevel.description') && !node.isCreateObjective
      ? 'group'
      : get(node, 'newEntityLevel.description')

  const isRootObjective = !node.parentId && node.entityType === ENTITIES.PLAN_OBJECTIVE
  const isObjective = node.entityType === ENTITIES.PLAN_OBJECTIVE
  const isAction = node.entityType === ENTITIES.PLAN_ACTION
  const isKPI = node.entityType === ENTITIES.PLAN_KPI

  if (node.isCreateRoot) {
    if (node.plan.locked) {
      return null
    }

    return (
      <AddRootEntityForm
        form={node.formName}
        level={node.firstLevel}
        rootType={node.rootType}
        onSubmit={node.handleRootCreateSubmit({ tree, treeIndex, path, setTree })}
      />
    )
  }

  if (node.isCreateObjective) {
    return (
      <AddEntityForm
        level={node.newEntityLevel}
        marginLeft={node.formMarginLeft}
        form={`CREATE_OBJECTIVE_${node.id}`}
        label={`Enter a description for this ${nextLevelDescription}`}
        onClose={node.handleCloseCreateForm({ tree, path, setTree, node })}
        onSubmit={node.handleObjectiveCreateSubmit({ tree, treeIndex, path, setTree, node })}
      />
    )
  }

  if (node.isCreateAction) {
    return (
      <AddEntityForm
        level={node.newEntityLevel}
        form={`CREATE_ACTION_${node.id}`}
        marginLeft={node.formMarginLeft}
        label={'Enter a description for this action'}
        onSubmit={node.handleActionCreateSubmit({ tree, treeIndex, path, setTree, node })}
        onClose={node.handleCloseCreateForm({ tree, path, setTree, node })}
      />
    )
  }

  if (node.isCreateKPI) {
    return (
      <AddEntityForm
        level={node.newEntityLevel}
        form={`CREATE_KPI_${node.id}`}
        marginLeft={node.formMarginLeft}
        label={'Enter a description for this indicator'}
        onSubmit={node.handleKPICreateSubmit({ tree, treeIndex, path, setTree, node })}
        onClose={node.handleCloseCreateForm({ tree, path, setTree, node })}
      />
    )
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return React.useMemo(
    () => (
      <Wrapper
        $isKPI={isKPI}
        $isAction={isAction}
        ref={connectDrag}
        $isObjective={isObjective}
        $isBeingDragged={isBeingDragged}
        $isNodeSearched={isNodeSearched}
        $isLandingPad={isLandingPadActive}
        $isSearchActive={search || hasFilter}
        $background={isRootObjective && color}
        $isCancelPad={isLandingPadActive && !canDrop}
        $isArchived={!!node.archived}
        {...otherProps}>
        <IconsWrapper
          $isKPI={isKPI}
          $canDrag={canDrag}
          $background={color}
          $isAction={isAction}
          $isRoot={isRootObjective}
          $isObjective={isObjective}
        >
          {hasChildren && isObjective && (
            <CollapseIcon onClick={handleCollapseClick} $isExpanded={node.expanded} color={'inherit'} />
          )}
        </IconsWrapper>
        <ContentWrapper
          $isKPI={isKPI}
          $background={color}
          $isAction={isAction}
          $isRoot={isRootObjective}
          $isObjective={isObjective}
          onClick={handleClick}>
          <NodeContent
            tree={tree}
            node={node}
            isTreeElement
            isKPI={isKPI}
            background={color}
            isAction={isAction}
            nodeTitle={nodeTitle}
            isRoot={isRootObjective}
            isObjective={isObjective}
            hasChildren={hasChildren}
            handleKPIAdd={handleKPIAdd}
            isRootObjective={isRootObjective}
            handleActionAdd={handleActionAdd}
            handleObjectiveAdd={handleObjectiveAdd}
            handleCollapseClick={handleCollapseClick}
            entity={{ id: node.entityId, entityType: generateLogEntityType(node.entityType) }}
          />
        </ContentWrapper>
      </Wrapper>
    ),
    [canDrag, node, parentNode, isNodeSearched, isLandingPadActive, tree, hasFilter]
  )
}

NodeContentRenderer.defaultProps = {
  buttons: [],
  canDrag: false,
  canDrop: false,
  className: '',
  draggedNode: null,
  icons: [],
  isSearchFocus: false,
  isSearchMatch: false,
  parentNode: null,
  rowDirection: 'ltr',
  style: {},
  subtitle: null,
  swapDepth: null,
  swapFrom: null,
  swapLength: null,
  title: null,
  toggleChildrenVisibility: null
}

// NodeContentRenderer.propTypes = {
//   buttons: arrayOf(node),
//   canDrag: bool,
//   className: string,
//   icons: arrayOf(node),
//   isSearchFocus: bool,
//   isSearchMatch: bool,
//   node: shape({}).isRequired,
//   path: arrayOf(oneOfType([string, number])).isRequired,
//   scaffoldBlockPxWidth: number.isRequired,
//   setTree: func,
//   style: shape({}),
//   subtitle: oneOfType([func, node]),
//   swapDepth: number,
//   swapFrom: number,
//   swapLength: number,
//   title: oneOfType([func, node]),
//   toggleChildrenVisibility: func,
//   tree: array,
//   treeId: string.isRequired,
//   treeIndex: number.isRequired,
//
//   // Drag and drop API functions
//   // Drag source
//   connectDragPreview: func.isRequired,
//   connectDragSource: func.isRequired,
//   didDrop: bool.isRequired,
//   draggedNode: shape({}),
//   isDragging: bool.isRequired,
//   parentNode: shape({}), // Needed for dndManager
//   // Drop target
//   canDrop: bool,
//   isOver: bool.isRequired,
//   rowDirection: string
// }

export default NodeContentRenderer
